/**
 * usePastCycleDetail
 *
 * Fetches a single closed cycle with its phases, medications, and metrics.
 * Combines useCycles + useCyclePhases into one convenient query.
 */

import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  useCycles,
  useCyclePhases,
  CYCLE_TYPE_LABELS,
  PHASE_TYPE_LABELS,
  type Cycle,
  type Phase,
} from "./useCycles";
import { extractObservedResults } from "../presenters/postCycleState";

// ---------------------------------------------------------------------------
// Types for the display layer
// ---------------------------------------------------------------------------

export interface PastCycleDisplay {
  id: string;
  cycleType: string;
  cycleTypeRaw: Cycle["cycleType"];
  cycleNumber: number;
  startDate: string | null;
  endDate: string | null;
  clinic?: string | null;
  protocol: string | null;
  description: string | null;
  status: Cycle["status"];
  outcome: string | null;
  cancellationReason: string | null;
  outcomeId: string | null;
  pregnancyOutcome: string | null;
  outcomeNotes: string | null;

  // Derived from phases
  medications: MedicationDisplay[];
  resultsFunnel: ResultEntry[];
  hormoneLevels: HormoneEntry[];
  stimSheet: StimDay[];
  journeyPhases: JourneyPhase[];
}

export interface MedicationDisplay {
  id: string;
  name: string;
  dosage: string;
  duration: string;
  frequency: string;
}

export interface ResultEntry {
  label: string;
  value: number;
}

export interface HormoneEntry {
  name: string;
  value: string;
  unit: string;
}

export interface StimDay {
  day: number;
  date?: string;
  e2?: number;
  lh?: number;
  p4?: number;
  follicles?: number;
  lining?: number;
  [medName: string]: number | string | undefined;
}

export interface JourneyPhase {
  label: string;
  type: string;
  startDate: string | null;
  endDate: string | null;
  isCompleted: boolean;
}

// ---------------------------------------------------------------------------
// Protocol labels
// ---------------------------------------------------------------------------

const PROTOCOL_LABELS: Record<string, string> = {
  gnrh_long_agonist: "Long Agonist",
  gnrh_short_agonist: "Short Agonist",
  gnrh_antagonist: "Antagonist",
  reduced_dose: "Reduced Dose",
  mild_stimulation: "Mild Stimulation",
  flare_up: "Flare-Up",
  microdose_flare: "Microdose Flare",
  natural: "Natural",
  modified_natural: "Modified Natural",
  clomiphene_citrate: "Clomiphene Citrate",
  letrozole: "Letrozole",
  gonadotropin: "Gonadotropin",
  clomiphene_iui: "Clomiphene IUI",
  tamoxifen: "Tamoxifen",
  combined_cc_tamoxifen: "CC + Tamoxifen",
  hrt: "HRT",
  artificial: "Artificial",
  other: "Other",
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePastCycleDetail(cycleId: string) {
  const cyclesQuery = useCycles();
  const phasesQuery = useCyclePhases(cycleId);

  const cycle = useMemo(() => {
    if (!cyclesQuery.data) return null;
    return cyclesQuery.data.find((c) => c.id === cycleId) ?? null;
  }, [cyclesQuery.data, cycleId]);

  const cycleNumber = useMemo(() => {
    if (!cyclesQuery.data || !cycle) return 1;
    const closed = cyclesQuery.data.filter((c) => c.status === "closed");
    const sameType = closed.filter((c) => c.cycleType === cycle.cycleType);
    const sorted = [...sameType].sort(
      (a, b) =>
        new Date(a.createdAt ?? "").getTime() -
        new Date(b.createdAt ?? "").getTime(),
    );
    const idx = sorted.findIndex((c) => c.id === cycleId);
    return idx >= 0 ? idx + 1 : 1;
  }, [cyclesQuery.data, cycle, cycleId]);

  const phases = useMemo(() => phasesQuery.data ?? [], [phasesQuery.data]);

  const display = useMemo<PastCycleDisplay | null>(() => {
    if (!cycle) return null;

    const outcomeObj = cycle.outcome;

    return {
      id: cycle.id,
      cycleType: CYCLE_TYPE_LABELS[cycle.cycleType] ?? cycle.cycleType,
      cycleTypeRaw: cycle.cycleType,
      cycleNumber,
      startDate: cycle.startDate,
      endDate: cycle.endDate,
      clinic: cycle.provider,
      protocol: cycle.protocol
        ? (PROTOCOL_LABELS[cycle.protocol] ?? cycle.protocol)
        : null,
      description: cycle.description,
      status: cycle.status,
      outcome: outcomeObj?.type ?? null,
      cancellationReason: outcomeObj?.cancellationReason ?? null,
      outcomeId: outcomeObj?.id ?? null,
      pregnancyOutcome: outcomeObj?.pregnancyOutcome ?? null,
      outcomeNotes: outcomeObj?.notes ?? null,

      medications: extractMedications(phases, cycle.startDate),
      resultsFunnel: extractResultsFunnel(phases),
      hormoneLevels: extractHormoneLevels(phases),
      stimSheet: extractStimSheet(phases, cycle.startDate),
      journeyPhases: extractJourneyPhases(phases),
    };
  }, [cycle, cycleNumber, phases]);

  return {
    cycle: display,
    rawCycle: cycle,
    phases,
    isLoading: cyclesQuery.isLoading || phasesQuery.isLoading,
    error: cyclesQuery.error || phasesQuery.error,
  };
}

// ---------------------------------------------------------------------------
// Extraction helpers (ported from Olly's cycleToTreatment.ts)
// ---------------------------------------------------------------------------

function extractResultsFunnel(phases: Phase[]): ResultEntry[] {
  const metricLabels: Record<string, string> = {
    retrieved_oocytes: "Eggs Retrieved",
    m2_count: "Mature Eggs (M2)",
    fertilized_oocytes: "Fertilized",
    embryos_day_3: "Day 3 embryos",
    embryos_day_5: "Blastocysts",
    frozen_embryo: "Embryos Frozen",
    transferred_embryo: "Embryos Transferred",
    follicles_over_17_mm: "Follicles ≥17mm",
  };

  return extractObservedResults(phases).map((result) => ({
    label: metricLabels[result.metric] ?? result.metric,
    value: result.value,
  }));
}

function extractMedications(
  phases: Phase[],
  cycleStartDate: string | null,
): MedicationDisplay[] {
  const meds: MedicationDisplay[] = [];
  const cycleStart = cycleStartDate ? new Date(cycleStartDate) : null;

  for (const phase of phases) {
    for (const med of phase.patientMedications ?? []) {
      const medInfo = med.medication;
      const name = medInfo?.name ?? med.rawMedicationName;
      if (!name) continue;

      const startDate = med.startDate ? new Date(med.startDate) : null;
      const endDate = med.endDate ? new Date(med.endDate) : null;

      const startDay =
        startDate && cycleStart
          ? Math.floor(
              (startDate.getTime() - cycleStart.getTime()) /
                (1000 * 60 * 60 * 24),
            ) + 1
          : 1;
      const endDay =
        endDate && cycleStart
          ? Math.floor(
              (endDate.getTime() - cycleStart.getTime()) /
                (1000 * 60 * 60 * 24),
            ) + 1
          : null;

      const duration = endDay
        ? `Day ${startDay}–${endDay} (${endDay - startDay + 1} days)`
        : `Day ${startDay}+`;

      const dose = med.dose ?? medInfo?.dose;
      const unit = med.doseUnit ?? medInfo?.doseUnit ?? "";
      const dosage = dose ? `${dose} ${unit}`.trim() : "As directed";

      const intakeType = med.route ?? null;
      const intakeLabels: Record<string, string> = {
        oral: "Daily (Oral)",
        subcutaneous: "Daily (Subcutaneous)",
        intramuscular: "Daily (IM)",
        vaginal: "Daily (Vaginal)",
        intravaginal: "Daily (Vaginal)",
        transdermal: "As directed (Patch)",
      };
      const frequency = intakeType
        ? (intakeLabels[intakeType] ?? `Daily (${intakeType})`)
        : "Daily";

      meds.push({ id: med.id, name, dosage, duration, frequency });
    }

    // Trigger shot
    const trigger = phase.triggerShot;
    if (trigger) {
      const date = trigger.date ? new Date(trigger.date) : null;
      const triggerDay =
        date && cycleStart
          ? Math.floor(
              (date.getTime() - cycleStart.getTime()) / (1000 * 60 * 60 * 24),
            ) + 1
          : null;

      meds.push({
        id: trigger.id ?? `trigger-${phase.id}`,
        name: "Trigger Shot",
        dosage: trigger.dose
          ? `${trigger.dose} ${trigger.doseUnit ?? ""}`.trim()
          : "As prescribed",
        duration: triggerDay ? `Day ${triggerDay} (1 day)` : "Trigger day",
        frequency: "Once (as scheduled)",
      });
    }
  }

  return meds;
}

function extractHormoneLevels(phases: Phase[]): HormoneEntry[] {
  const hormones: HormoneEntry[] = [];
  const seen = new Set<string>();

  for (const phase of phases) {
    for (const test of phase.testResults ?? []) {
      if (
        test.type === "blood" &&
        test.bloodTestCategory === "hormonal" &&
        test.name
      ) {
        const val = test.doctorValue ?? test.patientValue ?? test.value;
        if (val && !seen.has(test.name)) {
          seen.add(test.name);
          hormones.push({
            name: test.name,
            value: String(val),
            unit: test.unit ?? "",
          });
        }
      }
    }
  }

  return hormones;
}

function extractStimSheet(
  phases: Phase[],
  cycleStartDate: string | null,
): StimDay[] {
  const cycleStart = cycleStartDate ? new Date(cycleStartDate) : null;
  if (!cycleStart) return [];

  const dayMap = new Map<number, StimDay>();

  for (const phase of phases) {
    // Test results → E2, LH, P4
    for (const test of phase.testResults ?? []) {
      if (!test.date || test.type !== "blood") continue;
      const testDate = new Date(test.date);
      const day =
        Math.floor(
          (testDate.getTime() - cycleStart.getTime()) / (1000 * 60 * 60 * 24),
        ) + 1;
      if (day < 1) continue;

      if (!dayMap.has(day)) dayMap.set(day, { day, date: test.date });
      const entry = dayMap.get(day)!;

      const val = parseFloat(test.doctorValue ?? test.patientValue ?? "");
      if (isNaN(val)) continue;

      const nameLC = (test.name ?? "").toLowerCase();
      if (nameLC.includes("estradiol") || nameLC === "e2") {
        entry.e2 = val;
      } else if (nameLC.includes("lh") || nameLC === "lh") {
        entry.lh = val;
      } else if (
        nameLC.includes("progesterone") ||
        nameLC === "p4" ||
        nameLC === "prog"
      ) {
        entry.p4 = val;
      }
    }

    // Ovary follicles
    for (const f of phase.ovaryFollicles ?? []) {
      if (!f.date) continue;
      const fDate = new Date(f.date);
      const day =
        Math.floor(
          (fDate.getTime() - cycleStart.getTime()) / (1000 * 60 * 60 * 24),
        ) + 1;
      if (day < 1) continue;

      if (!dayMap.has(day)) dayMap.set(day, { day, date: f.date });
      const entry = dayMap.get(day)!;
      entry.follicles = (entry.follicles ?? 0) + (f.count ?? 1);
    }

    // Fertility metrics → lining
    for (const m of phase.fertilityMetrics ?? []) {
      if (m.type === "endometrial_thickness" && m.createdAt) {
        const mDate = new Date(m.createdAt);
        const day =
          Math.floor(
            (mDate.getTime() - cycleStart.getTime()) / (1000 * 60 * 60 * 24),
          ) + 1;
        if (day < 1) continue;

        if (!dayMap.has(day)) dayMap.set(day, { day, date: m.createdAt });
        dayMap.get(day)!.lining = Number(m.value);
      }
    }
  }

  return Array.from(dayMap.values()).sort((a, b) => a.day - b.day);
}

function extractJourneyPhases(phases: Phase[]): JourneyPhase[] {
  return phases
    .map((p) => ({
      label: PHASE_TYPE_LABELS[p.type] ?? p.type,
      type: p.type,
      startDate: p.startDate,
      endDate: p.endDate,
      isCompleted: !!p.startDate,
    }))
    .sort((a, b) => {
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      return a.startDate.localeCompare(b.startDate);
    });
}

// ---------------------------------------------------------------------------
// Pre-close snapshot — caches phase data before a cycle is closed
// ---------------------------------------------------------------------------

type PhasesApiClient = {
  GET(
    path: string,
    options: object,
  ): Promise<{ data?: { data: Phase[] } | null; error?: unknown }>;
};

export function useCreateTreatmentSnapshot() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cycleId: string) => {
      const { data } = await (client as unknown as PhasesApiClient).GET(
        "/{tenantId}/cycles/{id}/phases",
        { params: { path: { tenantId, id: cycleId } } },
      );
      const phases = data?.data ?? [];
      queryClient.setQueryData(["cycle-phases", tenantId, cycleId], phases);
      return phases;
    },
  });
}
